import * as React from 'react';
import { cloneDeep } from 'lodash';
import { message } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import {
  FrameItem,
  ParameterConfig,
  PromptInfo,
  PromptType
} from '@interface/index';
import '@style/component/response.less';
import ResizableEditor from '@component/business-component/resizable-editor/resizable-editor';
import './new-prompt-list.less';
import EmptyLine from '@component/business-component/empty-line/empty-line';
import { ParameterMapStore } from '@lib/parameter-map';

export interface NewPromptListCompProps {
  style?: React.CSSProperties;
  agentId?: string;
  frameId: string;
  ownerId: string;
  ownerType: string;
  promptType: PromptType;
  frame?: FrameItem;
  data: PromptInfo[];
  masterData?: PromptInfo[];
  delData?: PromptInfo[];
  parameters?: ParameterConfig[];
  bodyItTypeId?: string;
  update?: (next: PromptInfo[], oriData: PromptInfo[]) => void;
  placeholder?: string;
  placeholderFrist?: string;
  className?: string;
  borderTop?: boolean;
  disableChange?: boolean;
  noReference?: boolean;
  parameterMap: { [key: string]: ParameterConfig };
  isSingleLine?: boolean;
  allowClearAll?: Boolean;
  inModal?: boolean;
}

export interface PromptListHandle {
  add: (e?: any) => void;
}

const promptList = React.forwardRef<PromptListHandle, NewPromptListCompProps>(
  ({
    disableChange,
    agentId,
    frameId,
    ownerId,
    ownerType,
    promptType,
    frame,
    data,
    placeholder,
    update,
    inModal
  }, ref
  ) => {

    const editorRef = React.useRef<HTMLDivElement>(null);

    const [list, setList] = React.useState<PromptInfo[]>([]);
    const [editingIndex, setEditingIndex] = React.useState<number>(-1);
    const [editingCode, setEditingCode] = React.useState<string>('');

    const editingCodeRef = React.useRef<string>(editingCode);
    const editingIndexRef = React.useRef<number>(editingIndex);

    React.useEffect(() => {
      editingCodeRef.current = editingCode;
    }, [editingCode]);

    React.useEffect(() => {
      editingIndexRef.current = editingIndex;
    }, [editingIndex]);

    // 定义一个 ref 用来保存最新的 list
    const listRef = React.useRef<PromptInfo[]>(list);

    // 每次 list 更新时，同步更新 ref
    React.useEffect(() => {
      listRef.current = list;
    }, [list]);

    function resetList() {
      setList(data.filter((item: PromptInfo) => !item.toBeDeleted));
    }

    React.useEffect(() => {
      resetList();
    }, [data]);

    const [delayAdd, setDelayAdd] = React.useState<boolean>(false);
    // 定义一个 ref 用来保存最新的 list
    const delayAddRef = React.useRef<boolean>(delayAdd);

    // 每次 list 更新时，同步更新 ref
    React.useEffect(() => {
      delayAddRef.current = delayAdd;
    }, [delayAdd]);

    const add = (e?: any) => {

      if (delayAddRef.current) {
        setDelayAdd(false);
      } else if (inModal && editingIndex !== -1) {
        setDelayAdd(true);
        setTimeout(add);
        return;
      }

      if (disableChange) {
        message.warning('no permission to edit');
        return;
      }

      setEditingCode('');
      setEditingIndex(listRef.current.length);
      setTimeout(() => {
        editorRef.current?.focus();
      }, 100);
    };

    React.useImperativeHandle(ref, () => ({
      add
    }));

    function localUpdate(next: PromptInfo[]) {

      const originData = data || [];
      const nextData = cloneDeep(next.filter((item: PromptInfo) =>
        item.prompt !== ''));

      for (const oldItem of originData) {
        if (!next.find((item: PromptInfo) =>
          item.id === oldItem.id)) {
          const delItem = {
            ...oldItem,
            toBeDeleted: true
          };
          nextData.push(delItem);
        }
      }

      if (update) {
        update(nextData, originData);
      }
    }

    const cancel = () => {
      setEditingIndex(-1);
    };

    return <>{list.length === 0 && editingIndex === -1 ? <EmptyLine
      text={placeholder ?? 'Click to add first item.'}
      onClick={add}
    />
      : <div className="new-prompt-list">
        {
          list.map((item: PromptInfo, index: number) => {

            return (editingIndex === index) ?
              <div className="editor-box" ref={editorRef} key={index} >
                <ResizableEditor
                  completionContext={frame ? {
                    rawCodeType: frame
                  } : undefined}
                  ref={editorRef}
                  dataId={item.id}
                  initialValue={editingCode}
                  language={editingCode.trimStart().startsWith('{') ? 'customJson' : 'kotlinTemplate'}
                  onChange={(nextValue: string) => {
                    setEditingCode(nextValue);
                  }}
                  onBlur={() => {
                    cancel();
                  }}
                  onSave={() => {
                    const nextList = cloneDeep(list);
                    nextList[index].prompt = editingCodeRef.current;
                    setList(nextList);
                    localUpdate(nextList);
                    setEditingIndex(-1);
                  }}
                  onCancel={() => {
                    cancel();
                  }}
                />
              </div>
              : <div
                key={index}
                className="new-prompt-list-item"
                onClick={(e: any) => {
                  if (disableChange) {
                    return;
                  }
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingCode(item.prompt);
                  setEditingIndex(index);
                  setTimeout(() => {
                    editorRef.current?.focus();
                  }, 100);
                }}><span className="new-prompt-list-item-text">{item.prompt}</span>
                {!disableChange ? <button className="delete-button" onClick={(e: any) => {
                  e.preventDefault();
                  e.stopPropagation();

                  if (disableChange) {
                    message.warning('no permission to edit');
                    return;
                  }
                  const nextList = cloneDeep(list);
                  nextList.splice(index, 1);
                  setList(nextList);
                  localUpdate(nextList);

                }}>
                  <DeleteOutlined />
                </button> : ''
                }
              </div>;
          })
        }
        {editingIndex === list.length &&
          <div className="editor-box" ref={editorRef} key="new" >
            <ResizableEditor
              completionContext={frame ? {
                rawCodeType: frame
              } : undefined}
              ref={editorRef}
              dataId={'new'}
              initialValue={editingCode}
              language={editingCode.trimStart().startsWith('{') ? 'customJson' : 'kotlinTemplate'}
              onChange={(nextValue: string) => {
                setEditingCode(nextValue);
              }}
              onBlur={() => {
                cancel();
              }}
              onSave={() => {
                const nextList = cloneDeep(listRef.current);
                nextList.push({
                  agentId,
                  frameId,
                  ownerId,
                  ownerType,
                  promptType,
                  id: '',
                  prompt: editingCodeRef.current
                });
                setList(nextList);
                localUpdate(nextList);
                setEditingIndex(-1);
              }}
              onCancel={() => {
                cancel();
              }}
            />
          </div> || ''
        }
      </div>
    }
    </>;
  });

export const NewPromptList = promptList;
